/**
 * AppleSignInButton — native "Sign in with Apple" (App Store guideline 4.8).
 *
 * Renders ONLY inside the Capacitor iOS app (returns null on web/Android)
 * — the flow uses the native ASAuthorization sheet via
 * @capacitor-community/apple-sign-in, so there is nothing to show in a
 * browser. Flow: server challenge (state+nonce) → native sheet → POST
 * identity token to /api/auth/apple/exchange → same JWT cookie/session
 * as every other login.
 */
import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const APPLE_CLIENT_ID = "com.thetrucktrackr.app";

export const AppleSignInButton = ({ onSuccess, className = "" }) => {
  const { loginWithApple } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios")) {
    return null;
  }

  const signIn = async () => {
    setBusy(true);
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const { data: c } = await api.post("/auth/apple/challenge");
      const result = await SignInWithApple.authorize({
        clientId: APPLE_CLIENT_ID,
        redirectURI: "",
        scopes: "email name",
        state: c.state,
        nonce: c.nonce,
      });
      const r = result?.response || result || {};
      if (!r.identityToken) return; // user cancelled the sheet
      const res = await loginWithApple({
        identity_token: r.identityToken,
        state: c.state,
        nonce: c.nonce,
        email: r.email || null,
        given_name: r.givenName || null,
        family_name: r.familyName || null,
      });
      if (!res.ok) {
        toast.error(res.error || "Apple sign-in failed — try again.");
        return;
      }
      toast.success("Signed in.");
      if (onSuccess) onSuccess();
    } catch (e) {
      // Cancelled sheet throws on some iOS versions — stay silent for those.
      const msg = String(e?.message || e || "");
      if (!/cancel/i.test(msg)) toast.error("Apple sign-in failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={signIn}
      disabled={busy}
      data-testid="apple-signin-btn"
      className={`w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-black text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 ${className}`}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
        <path d="M16.365 1.43c0 1.14-.46 2.21-1.21 3.02-.79.87-2.1 1.54-3.16 1.46-.13-1.1.43-2.28 1.15-3.03.8-.86 2.19-1.5 3.22-1.45zM20.94 17.05c-.55 1.26-.82 1.82-1.53 2.94-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1.01-4.03-1-2.09.01-2.53 1.03-4.07 1.01-1.73-.02-3.05-1.77-4.04-3.32C.4 16.6-.13 11.6 1.5 8.94c1.16-1.89 2.99-3 4.71-3 1.75 0 2.85 1.02 4.3 1.02 1.4 0 2.26-1.02 4.28-1.02 1.53 0 3.15.83 4.3 2.27-3.78 2.07-3.17 7.46 1.85 8.84z" />
      </svg>
      {busy ? "Signing in…" : "Continue with Apple"}
    </button>
  );
};

export default AppleSignInButton;
